(() => {
  const html = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionKey = 'inthatpack-motion';
  const arrivalKey = 'inthatpack-arrival';
  const isV11 = document.body.classList.contains('itp-v11');
  let motionPreference = null;
  let navigationTimer = 0;
  let navigationRecovery = 0;
  let navigationPending = false;
  let activeViewTransition;
  let lastAuthTrigger;
  let closeDialogTimer = 0;
  try { const saved = window.localStorage.getItem(motionKey); if (saved === 'on' || saved === 'off') motionPreference = saved; } catch {}

  const motionEnabled = () => html.dataset.readingCalm !== 'on' && (motionPreference === 'on' || (motionPreference !== 'off' && !reducedMotion.matches));
  const updateMotion = () => {
    const enabled = motionEnabled();
    html.dataset.motion = enabled ? 'on' : 'off';
    html.dataset.motionChoice = motionPreference || 'system';
    html.dataset.motionSystem = reducedMotion.matches ? 'reduced' : 'normal';
    document.querySelectorAll('button[data-motion-system]').forEach(button => { button.hidden = false; button.setAttribute('aria-pressed', String(motionPreference === null)); });
    document.querySelectorAll('[data-motion-help]').forEach(note => { note.hidden = !reducedMotion.matches; note.textContent = motionPreference === 'on' ? note.dataset.labelChosen : note.dataset.labelSystem; });
    document.querySelectorAll('[data-motion-toggle]').forEach(button => {
      button.hidden = false;
      button.setAttribute('aria-pressed', String(enabled));
      button.disabled = html.dataset.readingCalm === 'on';
      const label = button.querySelector('[data-motion-label]');
      if (label) label.textContent = html.dataset.readingCalm === 'on' && button.dataset.labelCalm ? button.dataset.labelCalm : enabled ? button.dataset.labelOn : button.dataset.labelOff;
    });
    if (!enabled) document.querySelectorAll('[data-tilt]').forEach(element => {
      element.style.setProperty('--itp-tilt-x', '0deg');
      element.style.setProperty('--itp-tilt-y', '0deg');
    });
  };

  const transitionUpdate = update => {
    if (!motionEnabled() || typeof document.startViewTransition !== 'function') { update(); return; }
    try {
      if (activeViewTransition) activeViewTransition.skipTransition();
      activeViewTransition = document.startViewTransition(update);
      activeViewTransition.finished.catch(() => {});
    } catch { update(); }
  };

  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      if (motionEnabled()) entry.target.classList.add('itp-reveal-animate');
      entry.target.dataset.revealed = 'true';
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -24px 0px' }) : null;

  const setup = (root = document) => {
    const toggle = root.querySelector('[data-menu-toggle]');
    const menu = root.querySelector('[data-primary-nav]');
    if (toggle && menu && !toggle.dataset.ready) {
      toggle.dataset.ready = 'true';
      const close = () => { toggle.setAttribute('aria-expanded', 'false'); menu.classList.remove('is-open'); document.body.classList.remove('menu-open'); };
      toggle.addEventListener('click', () => { const open = toggle.getAttribute('aria-expanded') !== 'true'; toggle.setAttribute('aria-expanded', String(open)); menu.classList.toggle('is-open', open); document.body.classList.toggle('menu-open', open); });
      document.addEventListener('keydown', event => { if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { close(); toggle.focus(); } });
      menu.addEventListener('click', event => { if (event.target.closest('a')) close(); });
      document.addEventListener('click', event => {
        if (toggle.getAttribute('aria-expanded') === 'true' && !menu.contains(event.target) && !toggle.contains(event.target)) close();
      });
      document.addEventListener('focusin', event => {
        if (toggle.getAttribute('aria-expanded') === 'true' && !event.target.closest('.site-header')) close();
      });
      window.matchMedia('(min-width: 1201px)').addEventListener('change', event => { if (event.matches) close(); });
    }

    root.querySelectorAll('[data-register]').forEach(register => {
      if (register.dataset.ready) return;
      register.dataset.ready = 'true';
      const cards = Array.from(register.querySelectorAll('[data-recognition]'));
      const search = register.querySelector('[data-register-search]');
      const filters = Array.from(register.querySelectorAll('[data-filter]'));
      const empty = register.querySelector('[data-no-results]');
      const count = register.querySelector('[data-result-count]');
      let branch = 'all';
      const filter = () => {
        const term = (search?.value || '').trim().toLocaleLowerCase();
        let total = 0;
        cards.forEach(card => { const match = (branch === 'all' || card.dataset.branch === branch) && card.textContent.toLocaleLowerCase().includes(term); card.hidden = !match; if (match) total++; });
        if (empty) empty.hidden = total !== 0;
        if (count) count.textContent = String(total);
      };
      filters.forEach(button => button.addEventListener('click', () => { branch = button.dataset.filter; filters.forEach(item => item.setAttribute('aria-pressed', String(item === button))); filter(); }));
      if (search) search.addEventListener('input', filter);
    });

    root.querySelectorAll('[data-achievements-directory]').forEach(directory => {
      if (directory.dataset.directoryReady) return;
      directory.dataset.directoryReady = 'true';
      const cards = Array.from(directory.querySelectorAll('[data-directory-card]'));
      const search = directory.querySelector('[data-achievement-search]');
      const filters = Array.from(directory.querySelectorAll('[data-directory-filter]'));
      const empty = directory.querySelector('[data-directory-empty]');
      const count = directory.querySelector('[data-directory-count]');
      let kind = 'all';
      const filter = () => {
        const term = (search?.value || '').trim().toLocaleLowerCase();
        let total = 0;
        cards.forEach(card => {
          const match = (kind === 'all' || card.dataset.directoryKind === kind) && card.textContent.toLocaleLowerCase().includes(term);
          card.hidden = !match;
          if (match) total++;
        });
        if (empty) empty.hidden = cards.length === 0 || total > 0;
        if (count) count.textContent = String(total);
        directory.querySelectorAll('[data-directory-group]').forEach(group => {
          const groupCards = Array.from(group.querySelectorAll('[data-directory-card]'));
          group.hidden = groupCards.length > 0 && groupCards.every(card => card.hidden);
        });
      };
      filters.forEach(button => button.addEventListener('click', () => {
        kind = button.dataset.directoryFilter;
        filters.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        transitionUpdate(filter);
      }));
      if (search && !directory.hasAttribute('data-public-search')) search.addEventListener('input', filter);
      filter();
    });

    root.querySelectorAll('[data-share]').forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', async () => {
        const url = new URL(window.location.href); url.search = ''; url.hash = '';
        const status = button.parentElement.querySelector('[data-share-status]');
        try {
          if (navigator.share) await navigator.share({ title: document.title, url: url.href });
          else if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(url.href); if (status) status.textContent = button.dataset.copied; }
          else if (status) status.textContent = url.href;
        } catch (error) { if (error.name !== 'AbortError' && status) status.textContent = url.href; }
      });
    });

    root.querySelectorAll('[data-reveal],.photo-frame,.recognition-card,.cta-row,.form-panel,.accordion').forEach(element => {
      if (element.dataset.revealReady) return;
      element.dataset.revealReady = 'true';
      if (document.body.classList.contains('itp-v15') || !revealObserver || !motionEnabled() || element.contains(document.activeElement) || element.getBoundingClientRect().top < window.innerHeight - 24) {
        element.dataset.revealed = 'true';
        return;
      }
      revealObserver.observe(element);
    });

    if (!isV11) root.querySelectorAll('[data-tilt]').forEach(element => {
      if (element.dataset.tiltReady) return;
      element.dataset.tiltReady = 'true';
      let frame = 0;
      element.addEventListener('pointermove', event => {
        if (!finePointer.matches || !motionEnabled() || event.pointerType === 'touch') return;
        const bounds = element.getBoundingClientRect();
        const x = Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1));
        const y = Math.max(-1, Math.min(1, (event.clientY - bounds.top) / bounds.height * 2 - 1));
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          element.style.setProperty('--itp-tilt-x', `${(-y * 2).toFixed(2)}deg`);
          element.style.setProperty('--itp-tilt-y', `${(x * 2).toFixed(2)}deg`);
        });
      });
      element.addEventListener('pointerleave', () => {
        window.cancelAnimationFrame(frame);
        element.style.setProperty('--itp-tilt-x', '0deg');
        element.style.setProperty('--itp-tilt-y', '0deg');
      });
    });
    updateMotion();
  };

  const clearNavigation = () => {
    window.clearTimeout(navigationTimer);
    window.clearTimeout(navigationRecovery);
    navigationPending = false;
    document.querySelector('[data-page-transition]')?.classList.remove('is-departing', 'is-arriving');
    html.classList.remove('itp-is-leaving');
  };

  const closeAuthDialog = () => {
    const dialog = document.querySelector('#InthatpackAuthDialog');
    if (!dialog?.open) return;
    window.clearTimeout(closeDialogTimer);
    dialog.classList.add('is-closing');
    const finish = () => {
      dialog.close();
      dialog.classList.remove('is-closing');
      document.body.classList.remove('itp-auth-open');
      if (lastAuthTrigger?.isConnected) lastAuthTrigger.focus({ preventScroll: true });
    };
    if (motionEnabled()) closeDialogTimer = window.setTimeout(finish, 160);
    else finish();
  };

  const dialog = document.querySelector('#InthatpackAuthDialog');
  if (dialog) {
    dialog.addEventListener('cancel', event => { event.preventDefault(); closeAuthDialog(); });
    dialog.addEventListener('close', () => { document.body.classList.remove('itp-auth-open'); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeAuthDialog();
    });
  }

  const achievementListReturn = () => {
    const currentPage = new URL(document.baseURI);
    const validLists = ['automotive', 'entrepreneurship', 'watches', 'experiences'];
    const validCategories = ['supercar','car-65000','motorcycle','watch','jewellery','accessory','hotel','hotel-suite','fine-dining','travel','business-class','first-class','private-flight','villa','boat','yacht','private-jet','supercar-collection','watch-collection','business','creator','premium-tech','motorsport','art','craftsmanship'];
    const validStories = ['porsche','watch','housekeeping','business','journey','address'];
    const path = currentPage.pathname;
    const view = currentPage.searchParams.get('view');
    const returnPage = new URL(currentPage.pathname, window.location.origin);
    if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/achievements$/.test(path)) {
      if (['achievement-list','achievement-profile'].includes(view)) {
        returnPage.searchParams.set('view', view);
        const selectedList = currentPage.searchParams.get('list');
        if (validLists.includes(selectedList)) returnPage.searchParams.set('list', selectedList);
        const key = view === 'achievement-profile' ? 'rank' : 'rank_page';
        const value = currentPage.searchParams.get(key);
        const maximum = key === 'rank' ? 1200 : 48;
        if (value && /^\d{1,4}$/.test(value) && Number(value) > 0 && Number(value) <= maximum) returnPage.searchParams.set(key, value);
      } else if (view === 'achievement-category') {
        returnPage.searchParams.set('view', view);
        const category = currentPage.searchParams.get('category');
        if (validCategories.includes(category)) returnPage.searchParams.set('category', category);
      } else if (view) return null;
    } else if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/honours$/.test(path) && view === 'honours-profile') {
      const person = currentPage.searchParams.get('person');
      if (!/^(?:[1-9]|1[0-9]|2[01])$/.test(person || '')) return null;
      returnPage.searchParams.set('view', view);
      returnPage.searchParams.set('person', person);
    } else if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/submit-achievement$/.test(path)) {
      const category = currentPage.searchParams.get('category');
      if (validCategories.includes(category)) returnPage.searchParams.set('category', category);
    } else if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/?)?$/.test(path) && ['home', 'entrance'].includes(view)) {
      returnPage.searchParams.set('view', view);
    } else if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/?)?$/.test(path) && view === 'story') {
      const story = currentPage.searchParams.get('story');
      if (!validStories.includes(story)) return null;
      returnPage.searchParams.set('view','story');
      returnPage.searchParams.set('story',story);
    } else return null;
    const chosenLevel = currentPage.searchParams.get('level');
    if (['bronze','gold','platinum','diamond'].includes(chosenLevel)) returnPage.searchParams.set('level', chosenLevel);
    const query = currentPage.searchParams.get('q');
    if (query && view === 'achievement-list') returnPage.searchParams.set('q', query.slice(0,120));
    return returnPage.pathname + returnPage.search;
  };

  const syncLocaleReturn = () => {
    const returnPath = achievementListReturn();
    if (!returnPath) return;
    document.querySelectorAll('#HeaderLocaleForm input[name="return_to"],#FooterLocaleForm input[name="return_to"]').forEach(input => { input.value = returnPath; });
  };
  syncLocaleReturn();
  window.addEventListener('popstate', syncLocaleReturn);
  document.addEventListener('submit', event => {
    if (['HeaderLocaleForm', 'FooterLocaleForm'].includes(event.target.id)) syncLocaleReturn();
  }, true);
  document.addEventListener('click', event => {
    if (event.target.closest('#HeaderLocaleForm button[name="language_code"],#FooterLocaleForm button[name="language_code"]')) syncLocaleReturn();
  }, true);

  document.addEventListener('click', event => {
    const accountLink = event.target.closest('a');
    const listPath = achievementListReturn();
    if (accountLink && listPath) {
      const destination = new URL(accountLink.href, window.location.origin);
      if (destination.origin === window.location.origin && destination.pathname === '/customer_authentication/login') {
        destination.searchParams.set('return_to', listPath);
        accountLink.href = destination.pathname + destination.search;
      }
    }
    const trigger = event.target.closest('[data-auth-trigger]');
    if (trigger) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const authDialog = document.querySelector('#InthatpackAuthDialog');
      if (!authDialog) return;
      if (listPath) {
        const loginPage = new URL(authDialog.dataset.loginUrl, window.location.origin);
        if (loginPage.origin === window.location.origin && loginPage.pathname === '/customer_authentication/login') {
          loginPage.searchParams.set('return_to', listPath);
          authDialog.dataset.loginUrl = loginPage.pathname + loginPage.search;
          authDialog.querySelectorAll('.itp-auth-dialog__primary, .itp-auth-dialog__signin a').forEach(link => { link.href = loginPage.pathname + loginPage.search; });
        }
      }
      if (typeof authDialog.showModal !== 'function') {
        if (trigger.tagName !== 'A') window.location.assign(authDialog.dataset.loginUrl);
        return;
      }
      event.preventDefault();
      window.clearTimeout(closeDialogTimer);
      authDialog.classList.remove('is-closing');
      lastAuthTrigger = trigger;
      if (!authDialog.open) authDialog.showModal();
      document.body.classList.add('itp-auth-open');
      authDialog.querySelector('[data-auth-focus]')?.focus({ preventScroll: true });
      return;
    }
    if (event.target.closest('[data-auth-close]')) { closeAuthDialog(); return; }
    if (event.target.closest('button[data-motion-system]')) { motionPreference = null; try { localStorage.removeItem(motionKey); } catch {} updateMotion(); return; }
    const motionToggle = event.target.closest('[data-motion-toggle]');
    if (motionToggle) {
      motionPreference = motionEnabled() ? 'off' : 'on';
      try { window.localStorage.setItem(motionKey, motionPreference); } catch {}
      updateMotion();
      return;
    }
    const link = event.target.closest('a[data-world-link]');
    if (isV11 || !link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self') || window.Shopify?.designMode || !motionEnabled()) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol) || /\/(?:account|customer_authentication|checkout|checkouts)(?:\/|$)/i.test(url.pathname)) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;
    const transition = document.querySelector('[data-page-transition]');
    if (!transition) return;
    event.preventDefault();
    if (navigationPending) return;
    navigationPending = true;
    const label = transition.querySelector('[data-transition-label]');
    if (label) label.textContent = link.dataset.worldLabel || link.querySelector('[data-world-title]')?.textContent || 'INTHATPACK';
    transition.classList.remove('is-arriving');
    transition.classList.add('is-departing');
    html.classList.add('itp-is-leaving');
    try { window.sessionStorage.setItem(arrivalKey, JSON.stringify({ path: url.pathname, at: Date.now() })); } catch {}
    navigationTimer = window.setTimeout(() => window.location.assign(url.href), 520);
    navigationRecovery = window.setTimeout(clearNavigation, 6000);
  });

  window.addEventListener('storage', event => {
    if (event.key !== motionKey && event.key !== null) return;
    try { if (event.storageArea !== window.localStorage) return; } catch { return; }
    motionPreference = event.newValue === 'on' || event.newValue === 'off' ? event.newValue : null;
    updateMotion();
  });
  reducedMotion.addEventListener('change', updateMotion);
  new MutationObserver(updateMotion).observe(html, { attributes: true, attributeFilter: ['data-reading-calm'] });
  window.addEventListener('pageshow', event => { if (event.persisted || navigationPending) clearNavigation(); });
  setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
  try {
    const arrival = JSON.parse(window.sessionStorage.getItem(arrivalKey) || 'null');
    window.sessionStorage.removeItem(arrivalKey);
    if (!isV11 && motionEnabled() && arrival?.path === window.location.pathname && Date.now() - arrival.at < 15000) {
      const transition = document.querySelector('[data-page-transition]');
      transition?.classList.add('is-arriving');
      window.setTimeout(() => transition?.classList.remove('is-arriving'), 700);
    }
  } catch {}
})();


